import {
  SOLVE_CANVAS_BOUNDS,
  phaseIndex,
  type SolvePhase,
} from "@playbookai/types";

/**
 * Single source of truth for SOLVE phase visual + layout metadata.
 *
 * Both `<PhaseSwimlanes>` (the lane-background overlay) and `<SolvePhaseStrip>`
 * (the top-center quick-jump pill) read from here so the two stay in sync. If
 * a future Hoshin-style theme refresh recolours a phase, only this file
 * changes.
 *
 * The colours are theme tokens, not literals (ADR-0092): each phase reads a
 * `--pb-*` channel, so `.pb-light` and `.pb-high-contrast` supply their own
 * values and the canvas never paints a hex that ignores the theme.
 */

export interface PhaseMeta {
  readonly accent: string; // CSS colour expression — circle bg + accent text colour
  readonly accentBg: string; // CSS colour expression — soft tint behind the phase lane
  readonly letter: string; // 'S' | 'O' | 'L' | 'V' | 'E'
  readonly subtitle: string;
}

export const PHASE_META: Record<SolvePhase, PhaseMeta> = {
  Specify: {
    accent: "hsl(var(--pb-cyan))",
    accentBg: "hsl(var(--pb-cyan) / 0.05)",
    letter: "S",
    subtitle: "Frame the problem precisely before chasing causes.",
  },
  Oppress: {
    accent: "hsl(var(--pb-amber))",
    accentBg: "hsl(var(--pb-amber) / 0.05)",
    letter: "O",
    subtitle: "Hold the line — contain the bleed while we investigate.",
  },
  Look: {
    accent: "hsl(var(--pb-purple))",
    accentBg: "hsl(var(--pb-purple) / 0.05)",
    letter: "L",
    subtitle: "Find the real root cause with structured analysis.",
  },
  Verify: {
    // Green is a wrapped token (a full `hsl()` value), so it mixes rather
    // than taking a slash-alpha.
    accent: "var(--pbds-green)",
    accentBg: "color-mix(in srgb, var(--pbds-green) 5%, transparent)",
    letter: "V",
    subtitle: "Score and validate countermeasures before we commit.",
  },
  Ensure: {
    accent: "hsl(var(--pb-red))",
    accentBg: "hsl(var(--pb-red) / 0.05)",
    letter: "E",
    subtitle: "Lock in the gains so the problem doesn't return.",
  },
};

/** Canvas geometry. Width = LANE_WIDTH × 5; height fixed at 2000. */
export const CANVAS_WIDTH = SOLVE_CANVAS_BOUNDS.width;
export const CANVAS_HEIGHT = SOLVE_CANVAS_BOUNDS.height;
export const LANE_WIDTH = CANVAS_WIDTH / 5;

/** Match the practical infinite-canvas camera range used by VSM and Hoshin. */
export const SOLVE_MIN_ZOOM = 0.05;
export const SOLVE_MAX_ZOOM = 1.5;
export const SOLVE_PAN_MARGIN = 16_000;

/**
 * The header strip at the top of each phase lane. 56 px tall — enough for the
 * circle (36 px) plus padding. The first widget in a lane lands below this.
 */
export const HEADER_HEIGHT = 56;

/** Inside-lane padding when computing canonical widget positions. */
export const LANE_PADDING_X = 32;
export const LANE_PADDING_Y = 40; // distance below the header card

/** Rough vertical extent of the typical "content area" for fit-bounds zooms. */
export const PHASE_CONTENT_HEIGHT = 1200;

/** Reference two-column widget grid geometry from ui-elements workbench. */
export const WIDGET_COLUMN_WIDTH = 352;
export const WIDGET_COLUMN_GAP = 48;
export const WIDGET_ROW_HEIGHT = 300;
export const WIDGET_ROW_GAP = 48;

export interface WidgetLayoutSlot {
  readonly col: 0 | 1 | "full";
  readonly row: number;
  readonly width: number;
  readonly height: number;
}

export interface WidgetLayoutEntry {
  readonly phase: SolvePhase;
  readonly layout: WidgetLayoutSlot;
}

/**
 * Canonical (x, y) of the first widget slot inside a phase lane. Used by
 * `seedDefaultWidgets` to place singleton widgets. When the widget catalogue
 * grows we'll extend each `WidgetDef` with a `layout: {row, col}` field; for
 * the current 3-widget walking-skeleton only row 0 / col 0 is exercised.
 */
export function canonicalSlot(phase: SolvePhase): { x: number; y: number } {
  return {
    x: phaseIndex(phase) * LANE_WIDTH + LANE_PADDING_X,
    y: HEADER_HEIGHT + LANE_PADDING_Y,
  };
}

/**
 * Canonical widget position in the reference two-column phase grid.
 * Sums actual preceding row heights so extra-tall widgets like Problem Tree
 * and Funnel preserve spacing for all phases.
 */
export function canonicalWidgetSlot(
  phase: SolvePhase,
  layout: WidgetLayoutSlot,
  peerLayouts: ReadonlyArray<WidgetLayoutEntry>,
): { x: number; y: number } {
  const base = canonicalSlot(phase);
  const x =
    layout.col === 1
      ? base.x + WIDGET_COLUMN_WIDTH + WIDGET_COLUMN_GAP
      : base.x;

  let y = base.y;
  for (let row = 0; row < layout.row; row += 1) {
    const rowHeight = peerLayouts
      .filter((entry) => entry.phase === phase && entry.layout.row === row)
      .reduce(
        (max, entry) => Math.max(max, entry.layout.height),
        WIDGET_ROW_HEIGHT,
      );
    y += rowHeight + WIDGET_ROW_GAP;
  }
  return { x, y };
}

/** Center-ish landing spot for extra widget instances added from the toolbar. */
export function landingWidgetSlot(
  phase: SolvePhase,
  rowOffset = 3,
): { x: number; y: number } {
  const laneLeft = phaseIndex(phase) * LANE_WIDTH;
  return {
    x: laneLeft + (LANE_WIDTH - WIDGET_COLUMN_WIDTH) / 2,
    y:
      canonicalSlot(phase).y + rowOffset * (WIDGET_ROW_HEIGHT + WIDGET_ROW_GAP),
  };
}

/** Rect used by `<SolvePhaseStrip>` to `fitBounds` when a phase is clicked. */
export function phaseRect(phase: SolvePhase): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  return {
    x: phaseIndex(phase) * LANE_WIDTH,
    y: 0,
    width: LANE_WIDTH,
    height: PHASE_CONTENT_HEIGHT,
  };
}
